import React, { useEffect, useState } from 'react';
import {
  Card,
  Button,
  Form,
  InputNumber,
  Switch,
  message,
  Space,
  Row,
  Col,
  Divider,
  Alert,
  Tag,
  Table,
  Input,
  Select,
  Popconfirm,
} from 'antd';
import { SaveOutlined, ReloadOutlined, PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import {
  fetchShippingRule,
  saveShippingRule,
} from '../../api/shippingRule';
import { fetchCategoryList } from '../../api/category';

const ShippingRulePage = () => {
  const [form] = Form.useForm();
  const [regionForm] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [regions, setRegions] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [rules, setRules] = useState(null);

  const loadCategories = async () => {
    try {
      const res = await fetchCategoryList({ page: 1, pageSize: 200 });
      const list = res?.data?.list || res?.data?.data?.list || [];
      setCategoryOptions(list.map((item) => ({ label: item.name, value: item.id })));
    } catch (error) {
      console.warn('获取分类失败:', error);
    }
  };

  const loadRules = async () => {
    setLoading(true);
    try {
      const res = await fetchShippingRule();
      if (res?.success) {
        const data = res.data || {};
        setRules(data);
        setRegions(data.conditions?.byRegion?.regions || []);
        form.setFieldsValue({
          enabled: data.enabled,
          freeShippingAmount: data.freeShippingAmount,
          defaultShippingFee: data.defaultShippingFee,
          byQuantityEnabled: data.conditions?.byQuantity?.enabled,
          byQuantityThreshold: data.conditions?.byQuantity?.threshold,
          byRegionEnabled: data.conditions?.byRegion?.enabled,
          byCategoryEnabled: data.conditions?.byCategory?.enabled,
          byCategoryIds: data.conditions?.byCategory?.categoryIds || [],
        });
      }
    } catch (error) {
      message.error('获取包邮规则失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRules();
    loadCategories();
  }, []);

  const handleAddRegion = () => {
    regionForm.validateFields().then(({ region }) => {
      const name = String(region).trim();
      if (!name) return;
      if (regions.includes(name)) {
        message.warning('该地区已添加');
        return;
      }
      setRegions((prev) => [...prev, name]);
      regionForm.setFieldsValue({ region: '' });
    });
  };

  const handleRemoveRegion = (region) => {
    setRegions((prev) => prev.filter((item) => item !== region));
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      const payload = {
        enabled: values.enabled,
        freeShippingAmount: values.freeShippingAmount,
        defaultShippingFee: values.defaultShippingFee,
        conditions: {
          byQuantity: {
            enabled: values.byQuantityEnabled,
            threshold: values.byQuantityEnabled ? values.byQuantityThreshold : 0,
          },
          byRegion: {
            enabled: values.byRegionEnabled,
            regions,
          },
          byCategory: {
            enabled: values.byCategoryEnabled,
            categoryIds: values.byCategoryEnabled ? values.byCategoryIds || [] : [],
          },
        },
      };
      setSaving(true);
      const res = await saveShippingRule(payload);
      if (res?.success) {
        message.success('包邮规则已保存');
        await loadRules();
      } else {
        message.error(res?.message || '保存失败');
      }
    } catch (error) {
      if (error?.errorFields) return;
      message.error(error?.appMessage || '保存失败');
    } finally {
      setSaving(false);
    }
  };

  const enabled = Form.useWatch('enabled', form);

  return (
    <div className="workspace-page">
      <div className="workspace-header">
        <div>
          <h1 className="workspace-title">包邮规则</h1>
          <p className="workspace-description">设置包邮门槛与默认运费，下单结算时自动生效</p>
        </div>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={loadRules}>刷新</Button>
          <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={handleSave}>
            保存规则
          </Button>
        </Space>
      </div>

      <Card loading={loading} style={{ marginTop: 16 }}>
        {!rules && !loading ? null : (
          <Form form={form} layout="vertical" style={{ maxWidth: 720 }}>
            <Form.Item name="enabled" valuePropName="checked" style={{ marginBottom: 16 }}>
              <Switch checkedChildren="已启用包邮规则" unCheckedChildren="未启用" />
            </Form.Item>

            {enabled && (
              <Alert
                type="info"
                showIcon
                style={{ marginBottom: 24 }}
                message="启用后，下单结算的运费将按以下规则自动计算"
              />
            )}

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="freeShippingAmount"
                  label="满额包邮金额（元）"
                  rules={[{ required: true, message: '请输入包邮金额' }]}
                >
                  <InputNumber min={0} max={999999} precision={2} style={{ width: '100%' }} placeholder="例如：99" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="defaultShippingFee"
                  label="默认运费（元）"
                  rules={[{ required: true, message: '请输入默认运费' }]}
                >
                  <InputNumber min={0} max={999} precision={2} style={{ width: '100%' }} placeholder="例如：10" />
                </Form.Item>
              </Col>
            </Row>

            <Divider orientation="left" style={{ fontSize: 14 }}>扩展包邮条件（可多选组合）</Divider>

            <Row gutter={[16, 16]}>
              <Col span={12}>
                <Card size="small" title="满件数包邮">
                  <Form.Item name="byQuantityEnabled" valuePropName="checked">
                    <Switch size="small" checkedChildren="启用" unCheckedChildren="停用" />
                  </Form.Item>
                  <Form.Item name="byQuantityThreshold" label="满多少件包邮" noStyle>
                    <InputNumber min={1} max={9999} style={{ width: '100%' }} placeholder="例如：5" />
                  </Form.Item>
                </Card>
              </Col>
              <Col span={12}>
                <Card size="small" title="指定分类包邮">
                  <Form.Item name="byCategoryEnabled" valuePropName="checked">
                    <Switch size="small" checkedChildren="启用" unCheckedChildren="停用" />
                  </Form.Item>
                  <Form.Item name="byCategoryIds" noStyle>
                    <Select
                      mode="multiple"
                      allowClear
                      placeholder="选择包邮分类"
                      options={categoryOptions}
                      style={{ width: '100%' }}
                    />
                  </Form.Item>
                </Card>
              </Col>
            </Row>

            <Card size="small" title="指定地区包邮" style={{ marginTop: 16 }}>
              <Form.Item name="byRegionEnabled" valuePropName="checked" style={{ marginBottom: 16 }}>
                <Switch size="small" checkedChildren="启用" unCheckedChildren="停用" />
              </Form.Item>
              <Space>
                <Form form={regionForm} component={false}>
                  <Form.Item name="region" noStyle>
                    <Input placeholder="输入地区，如：福建省" style={{ width: 220 }} onPressEnter={handleAddRegion} />
                  </Form.Item>
                </Form>
                <Button icon={<PlusOutlined />} onClick={handleAddRegion}>添加地区</Button>
              </Space>
              <div style={{ marginTop: 12 }}>
                {regions.length === 0 ? (
                  <span style={{ color: '#999', fontSize: 13 }}>暂无地区，收货地址前缀匹配该地区即包邮</span>
                ) : (
                  <Space wrap>
                    {regions.map((region) => (
                      <Tag
                        key={region}
                        closable
                        onClose={() => handleRemoveRegion(region)}
                        style={{ fontSize: 13, padding: '2px 8px' }}
                      >
                        {region}
                      </Tag>
                    ))}
                  </Space>
                )}
              </div>
            </Card>

            <Divider />

            <div>
              <Space>
                <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={handleSave}>
                  保存规则
                </Button>
                <Button onClick={loadRules}>重置</Button>
              </Space>
            </div>
          </Form>
        )}
      </Card>
    </div>
  );
};

export default ShippingRulePage;
